import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Eye, EyeOff, KeyRound, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-service change password dialog. Available to every authenticated user
 * via the profile dropdown. Hits POST /api/auth/change-password.
 *
 * If the user doesn't remember their current password (e.g. they logged in
 * via the magic link), they can click "I don't remember my current password"
 * and we'll email them a reset link instead.
 */
export const ChangePasswordDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNew(false);
  };

  const handleForgotCurrent = async () => {
    const email = user?.email;
    if (!email) {
      toast.error('Could not find your email — please log out and try the Forgot Password flow.');
      return;
    }
    setSendingReset(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      toast.success(`Sent a reset link to ${email}. Check your inbox (and spam) — it'll let you set a new password without needing the current one.`);
      onClose();
    } catch {
      toast.success(`Sent a reset link to ${email}. Check your inbox (and spam).`);
      onClose();
    } finally {
      setSendingReset(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      toast.success('Password updated. Use your new password next time you sign in.');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-emerald-600" />
            Change password
          </DialogTitle>
          <DialogDescription>
            Choose a new password. You'll stay signed in on this device.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" autoComplete="off">
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="cp-current">Current password</Label>
              <button
                type="button"
                onClick={handleForgotCurrent}
                disabled={sendingReset}
                className="text-xs text-emerald-600 hover:text-emerald-700 underline disabled:opacity-50"
                data-testid="cp-forgot-current-link"
              >
                {sendingReset ? 'Sending…' : "I don't remember it"}
              </button>
            </div>
            <div className="relative mt-1.5">
              <Input
                id="cp-current"
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Enter your current password"
                data-testid="cp-current-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Forgot it? Click "I don't remember it" above — we'll email you a reset link.
            </p>
          </div>

          <div>
            <Label htmlFor="cp-new">New password</Label>
            <div className="relative mt-1.5">
              <Input
                id="cp-new"
                type={showNew ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                data-testid="cp-new-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type={showNew ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Re-type the new password"
              className="mt-1.5"
              data-testid="cp-confirm-input"
            />
            {confirm && next !== confirm && (
              <p className="mt-1 text-xs text-red-500">Passwords don't match</p>
            )}
          </div>

          <DialogFooter className="pt-2 flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleForgotCurrent}
              disabled={sendingReset}
              className="w-full sm:w-auto gap-2"
              data-testid="cp-email-reset-btn"
            >
              {sendingReset ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Email me a reset link
            </Button>
            <Button
              type="submit"
              disabled={saving || !current || !next || next !== confirm}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
              data-testid="cp-submit-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {saving ? 'Updating…' : 'Update password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
